import { useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Workflow } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";

import {
  buildB2cBoard,
  canOpenNextIteration,
  earlierUnstarted,
  defaultIntegrationBranch,
  nextIterationNumber,
  type IterationView,
  type WorkstreamView,
} from "@/application/use-cases/b2c/build-b2c-board";
import {
  useB2cActivity,
  useB2cGates,
  useB2cIterationBranches,
  useB2cIterations,
  useB2cStages,
  useB2cTasks,
  useB2cWorkstreams,
  useCreateB2cIteration,
  useCreateB2cTask,
  useDeleteB2cTask,
  usePlanB2cTask,
  useUpdateB2cIteration,
  useUpdateB2cIterationBranch,
  useUpdateB2cTask,
  useUpsertB2cIterationBranch,
  useUpdateB2cStage,
  useUpdateB2cWorkstream,
} from "@/application/use-cases/b2c/use-b2c-board.queries";
import {
  EMPTY_B2C_FILTERS,
  filterB2cBoard,
  filtersTasks,
  isFiltering,
  type B2cBoardFilters,
} from "@/application/use-cases/b2c/filter-b2c-board";
import { B2cActivityFeed } from "@/presentation/components/b2c/b2c-activity-feed";
import type { BacklogActions } from "@/presentation/components/b2c/backlog-actions";
import { B2cBoardToolbar } from "@/presentation/components/b2c/b2c-board-toolbar";
import type { IterationActions } from "@/presentation/components/b2c/iteration-block";
import { StageSection } from "@/presentation/components/b2c/stage-section";
import { isCurrentStage } from "@/application/use-cases/b2c/is-current-stage";
import { B2cTaskDrawerContent } from "@/presentation/components/b2c/task-drawer-content";
import { WorkstreamCard } from "@/presentation/components/b2c/workstream-card";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { B2C_BOARD_SUMMARY } from "@/presentation/data/b2c-board";
import { useWorkspace } from "@/presentation/providers/workspace.provider";
import type { B2cRepo, B2cStageRow, B2cTaskRow } from "@/infrastructure/supabase/b2c-board.types";

// `?task=<id>` opens a task in the drawer, so a task can be linked to directly.
const searchSchema = z.object({ task: z.string().optional() });

export const Route = createFileRoute("/_app/b2c/")({
  validateSearch: searchSchema,
  component: B2cBoardPage,
});

function failsWith(what: string) {
  return { onError: () => toast.error(`${what} — you may not have permission.`) };
}

function B2cBoardPage() {
  const { canWrite } = useWorkspace();
  const stagesQ = useB2cStages();
  const workstreamsQ = useB2cWorkstreams();
  const gatesQ = useB2cGates();
  const iterationsQ = useB2cIterations();
  const branchesQ = useB2cIterationBranches();
  const tasksQ = useB2cTasks();

  const updateTask = useUpdateB2cTask();
  const createTask = useCreateB2cTask();
  const deleteTask = useDeleteB2cTask();
  const planTask = usePlanB2cTask();
  const createIteration = useCreateB2cIteration();
  const updateIteration = useUpdateB2cIteration();
  const upsertBranch = useUpsertB2cIterationBranch();
  const updateBranch = useUpdateB2cIterationBranch();
  const updateWorkstream = useUpdateB2cWorkstream();
  const updateStage = useUpdateB2cStage();

  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({});
  const [stageOverrides, setStageOverrides] = useState<Record<string, boolean>>({});
  const [activityOpen, setActivityOpen] = useState(false);
  const activityQ = useB2cActivity({ enabled: activityOpen });
  const navigate = useNavigate({ from: Route.fullPath });
  const { task: openTaskId } = Route.useSearch();

  const iterations = useMemo(() => iterationsQ.data ?? [], [iterationsQ.data]);
  const stagesByKey = useMemo(
    () => new Map((stagesQ.data ?? []).map((s) => [s.key, s])),
    [stagesQ.data]
  );
  const tasks = useMemo(() => tasksQ.data ?? [], [tasksQ.data]);
  const board = useMemo(
    () =>
      buildB2cBoard({
        stages: stagesQ.data ?? [],
        workstreams: workstreamsQ.data ?? [],
        gates: gatesQ.data ?? [],
        iterations,
        branches: branchesQ.data ?? [],
        tasks,
      }),
    [stagesQ.data, workstreamsQ.data, gatesQ.data, iterations, branchesQ.data, tasks]
  );

  const loading =
    stagesQ.isLoading ||
    workstreamsQ.isLoading ||
    gatesQ.isLoading ||
    iterationsQ.isLoading ||
    branchesQ.isLoading ||
    tasksQ.isLoading;
  const failed =
    stagesQ.isError ||
    workstreamsQ.isError ||
    gatesQ.isError ||
    iterationsQ.isError ||
    branchesQ.isError ||
    tasksQ.isError;

  const [filters, setFilters] = useState<B2cBoardFilters>(EMPTY_B2C_FILTERS);
  const visible = useMemo(() => filterB2cBoard(board, filters), [board, filters]);
  const hasAny = board.stages.some((s) => s.workstreams.length > 0) || board.unstaged.length > 0;

  const allViews = [...visible.stages.flatMap((s) => s.workstreams), ...visible.unstaged];
  // A workstream with a running marathon starts open, and so does every match
  // while a filter narrows the tasks; everything else starts collapsed.
  const isOpen = (v: WorkstreamView) =>
    openOverrides[v.workstream.key] ??
    (filtersTasks(filters) || v.current?.iteration.status === "marathon");
  // The current stage (today inside its window) starts open, and so does every stage while a
  // filter narrows the board; the rest start collapsed to one line.
  const today = new Date().toISOString().slice(0, 10);
  const isStageOpen = (stage: B2cStageRow) =>
    stageOverrides[stage.key] ?? (isFiltering(filters) || isCurrentStage(stage, today));
  const toggleStage = (stage: B2cStageRow) =>
    setStageOverrides((prev) => ({ ...prev, [stage.key]: !isStageOpen(stage) }));
  const toggle = (v: WorkstreamView) =>
    setOpenOverrides((prev) => ({ ...prev, [v.workstream.key]: !isOpen(v) }));
  const allOpen = allViews.length > 0 && allViews.every(isOpen);
  const setAll = (open: boolean) =>
    setOpenOverrides(Object.fromEntries(allViews.map((v) => [v.workstream.key, open])));

  const now = () => new Date().toISOString();

  const startBlockedReasonFor = (view: WorkstreamView) => (iv: IterationView) => {
    const running = view.iterations.find(
      (other) => other.iteration.status === "marathon" && other.iteration.id !== iv.iteration.id
    );
    if (running) return `Iteration ${running.iteration.number}'s marathon is still running.`;
    const unstarted = earlierUnstarted(
      view.iterations.map((i) => i.iteration),
      iv.iteration.number
    );
    if (unstarted !== null) return `Start iteration ${unstarted} first — this one stacks on it.`;
    if (!view.gateOpen) {
      const waiting = view.buildsOn.filter((g) => !g.merged).map((g) => g.code);
      return `Waiting for ${waiting.join(", ")} to merge before this flow can start.`;
    }
    return null;
  };

  // The lowest open iteration after this one; created when there is none.
  // One in-flight "create the next iteration" per workstream: `view` comes from the
  // render closure, so two quick moves would otherwise both see no next iteration
  // and both insert the same number (the second hits the unique constraint).
  const creatingNext = useRef(new Map<string, Promise<string>>());
  // Both "New iteration" and "move to next iteration" create through here.
  const createNextIteration = (view: WorkstreamView): Promise<string> => {
    const key = view.workstream.key;
    const pending = creatingNext.current.get(key);
    if (pending) return pending;
    const created = createIteration
      .mutateAsync({
        workstreamKey: key,
        number: nextIterationNumber(view.iterations.map((i) => i.iteration)),
      })
      .then((row) => row.id)
      .finally(() => creatingNext.current.delete(key));
    creatingNext.current.set(key, created);
    return created;
  };

  const nextIterationAfter = (view: WorkstreamView, after: number): Promise<string> => {
    const existing = view.iterations.find(
      (i) => i.iteration.number > after && i.iteration.status !== "merged"
    );
    if (existing) return Promise.resolve(existing.iteration.id);
    return createNextIteration(view);
  };

  const actionsFor =
    (view: WorkstreamView) =>
    (iv: IterationView): IterationActions => {
      const blocked = startBlockedReasonFor(view)(iv);
      const defaultBranch = defaultIntegrationBranch(view.workstream.code, iv.iteration.number);
      const branchOf = (repo: B2cRepo) => iv.branches.find((b) => b.branch.repo === repo)?.branch;
      // Every repo uses the same branch name; a repo's row is added when the marathon opens it.
      const upsertRepo = (repo: B2cRepo, patch: { pr_url?: string | null }, what: string) =>
        upsertBranch.mutate(
          {
            iterationId: iv.iteration.id,
            repo,
            patch: { branch: branchOf(repo)?.branch ?? defaultBranch, ...patch },
          },
          failsWith(what)
        );
      return {
        onStart: blocked
          ? null
          : () =>
              updateIteration.mutate(
                {
                  id: iv.iteration.id,
                  patch: { status: "marathon", marathon_started_at: now() },
                },
                {
                  ...failsWith("Couldn’t start the marathon"),
                  onSuccess: () => {
                    if (!branchOf("yango-app"))
                      upsertRepo("yango-app", {}, "Couldn’t record the branch");
                  },
                }
              ),
        onFinish: (outputUrl) =>
          updateIteration.mutate(
            {
              id: iv.iteration.id,
              patch: { status: "feedback", marathon_finished_at: now(), output_url: outputUrl },
            },
            failsWith("Couldn’t close the marathon")
          ),
        onMerge: () =>
          updateIteration.mutate(
            { id: iv.iteration.id, patch: { status: "merged", merged_at: now() } },
            failsWith("Couldn’t mark the iteration merged")
          ),
        onAddTask: (title) => {
          const last = iv.tasks.reduce((max, t) => Math.max(max, t.sort_order), 0);
          createTask.mutate(
            {
              title,
              workstream_key: view.workstream.key,
              iteration_id: iv.iteration.id,
              sort_order: last + 10,
            },
            failsWith("Couldn’t add the task")
          );
        },
        onTaskStatus: (task: B2cTaskRow, status) =>
          updateTask.mutate(
            { id: task.id, patch: { status: status } },
            failsWith("Couldn’t save the status")
          ),
        onOpenTask: (task: B2cTaskRow) => void navigate({ search: { task: task.id } }),
        onSetBranchPr: (repo, url) =>
          upsertRepo(repo, { pr_url: url }, "Couldn’t save the PR link"),
        onAddRepo: (repo) => upsertRepo(repo, {}, "Couldn’t add the repo"),
        onMarkBranchMerged: (repo) => {
          const row = branchOf(repo);
          if (!row) return;
          updateBranch.mutate(
            { id: row.id, patch: { merged_at: now() } },
            failsWith("Couldn’t mark the branch merged")
          );
        },
        onMoveToNext: (task: B2cTaskRow) => {
          void (async () => {
            try {
              const target = await nextIterationAfter(view, iv.iteration.number);
              await updateTask.mutateAsync({ id: task.id, patch: { iteration_id: target } });
              toast.success(`${task.code} moved to the next iteration`);
            } catch {
              toast.error("Couldn’t move the task — you may not have permission.");
            }
          })();
        },
      };
    };

  const backlogActionsFor = (view: WorkstreamView): BacklogActions => ({
    onPlan: (task, iterationId) => {
      const target = view.iterations.find((i) => i.iteration.id === iterationId);
      if (!target) return;
      const last = target.tasks.reduce((max, t) => Math.max(max, t.sort_order), 0);
      planTask.mutate(
        { id: task.id, iterationId, sortOrder: last + 10 },
        {
          // Not failsWith: the write also declines a task another editor has
          // planned, or closed, since the panel last read it.
          onError: () =>
            toast.error(
              `Couldn’t add ${task.code} — it may have moved or closed since, or you may not have permission.`
            ),
          onSuccess: () =>
            toast.success(`${task.code} added to iteration ${target.iteration.number}`),
        }
      );
    },
    onReject: (task) =>
      deleteTask.mutate(task.id, {
        // Not failsWith: the delete also declines a task another editor has
        // planned, or closed, since the panel last read it.
        onError: () =>
          toast.error(
            `Couldn’t reject ${task.code} — it may have moved into an iteration since, or you may not have permission.`
          ),
        onSuccess: () => toast.success(`${task.code} rejected`),
      }),
    onOpenTask: (task) => void navigate({ search: { task: task.id } }),
  });

  const renderCard = (view: WorkstreamView) => {
    // Iterations stack: a new one can open while earlier ones are still running or
    // waiting for feedback — just not while one is still waiting to start.
    const canOpenNext = canOpenNextIteration(view.iterations.map((i) => i.iteration));
    return (
      <WorkstreamCard
        key={view.workstream.key}
        view={view}
        canWrite={canWrite}
        open={isOpen(view)}
        onToggle={() => toggle(view)}
        onPatch={(patch) =>
          updateWorkstream.mutate(
            { key: view.workstream.key, patch },
            failsWith("Couldn’t save the workstream")
          )
        }
        onNewIteration={
          !canOpenNext
            ? null
            : () =>
                void createNextIteration(view).catch(() =>
                  toast.error("Couldn’t create the iteration — you may not have permission.")
                )
        }
        actionsFor={actionsFor(view)}
        startBlockedReasonFor={startBlockedReasonFor(view)}
        backlogActions={backlogActionsFor(view)}
      />
    );
  };

  const openTask = openTaskId ? (tasks.find((t) => t.id === openTaskId) ?? null) : null;
  const { summary } = board;
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <RouteHero
        eyebrow="B2C MVP · cutover 1 December"
        icon={Workflow}
        numeral={String(summary.marathons).padStart(2, "0")}
        numeralTone="amber"
        numeralSuffix={`/ ${String(summary.workstreams).padStart(2, "0")}`}
        title="Marathons running"
        summary={B2C_BOARD_SUMMARY}
        collapsibleSummary
        meta={[
          { label: "Workstreams", value: summary.workstreams },
          { label: "Marathons", value: summary.marathons, tone: "amber" },
          { label: "Awaiting feedback", value: summary.awaitingFeedback, tone: "cyan" },
          {
            label: "Tasks done",
            value: `${summary.progress.done}/${summary.progress.total}`,
            tone: "green",
          },
        ]}
      />

      <B2cBoardToolbar
        filters={filters}
        onFiltersChange={setFilters}
        stages={stagesQ.data ?? []}
        allOpen={allOpen}
        onToggleAll={() => setAll(!allOpen)}
        onOpenActivity={() => setActivityOpen(true)}
      />

      {loading ? (
        <p className="text-muted-foreground py-8 text-center text-xs italic">Loading…</p>
      ) : failed ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          The B2C board couldn’t load. You may not have access to the B2C implementation workspace.
        </p>
      ) : !hasAny ? (
        <p className="text-muted-foreground py-8 text-center text-sm">No B2C workstreams yet.</p>
      ) : allViews.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-[13px]">
          Nothing matches the current filters.
        </p>
      ) : (
        <div className="flex flex-col gap-12">
          {visible.stages.map((sv) => (
            <StageSection
              key={sv.stage.key}
              stage={sv.stage}
              progress={sv.progress}
              workstreamCodes={sv.workstreams.map((v) => v.workstream.code)}
              open={isStageOpen(sv.stage)}
              onToggle={() => toggleStage(sv.stage)}
              canWrite={canWrite}
              onPatch={(patch) =>
                updateStage.mutate(
                  { key: sv.stage.key, patch },
                  failsWith("Couldn’t save the stage")
                )
              }
            >
              {sv.workstreams.map(renderCard)}
            </StageSection>
          ))}
          {visible.unstaged.length > 0 ? (
            <section className="flex flex-col gap-4">{visible.unstaged.map(renderCard)}</section>
          ) : null}
        </div>
      )}

      <Sheet
        open={!!openTaskId}
        onOpenChange={(next) => {
          if (!next) void navigate({ search: {} });
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
          <SheetTitle className="sr-only">Task detail</SheetTitle>
          <SheetDescription className="sr-only">View and edit the B2C task.</SheetDescription>
          {openTask ? (
            <B2cTaskDrawerContent
              task={openTask}
              workstream={
                (workstreamsQ.data ?? []).find((w) => w.key === openTask.workstream_key) ?? null
              }
              iterations={iterations
                .filter((i) => i.workstream_key === openTask.workstream_key)
                .sort((a, b) => a.number - b.number)}
              stagesByKey={stagesByKey}
              canWrite={canWrite}
              onPatch={(patch) =>
                updateTask.mutate({ id: openTask.id, patch }, failsWith("Couldn’t save the change"))
              }
            />
          ) : (
            <p className="text-muted-foreground p-6 text-xs italic">
              {tasksQ.isLoading ? "Loading task…" : "That task no longer exists."}
            </p>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={activityOpen} onOpenChange={setActivityOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
          <div className="border-border/60 border-b px-6 py-5">
            <SheetTitle className="text-[15px] font-semibold tracking-tight">
              B2C board activity
            </SheetTitle>
            <SheetDescription className="text-muted-foreground mt-1 text-[12px]">
              Every change across the B2C board, newest first — who did what, and when.
            </SheetDescription>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-3">
            <B2cActivityFeed
              entries={(activityQ.data?.pages ?? []).flatMap((p) => p.rows)}
              isLoading={activityQ.isLoading}
              isError={activityQ.isError}
              stagesByKey={stagesByKey}
              showSubject
              onOpenTask={(taskId) => {
                setActivityOpen(false);
                void navigate({ search: { task: taskId } });
              }}
              hasMore={activityQ.hasNextPage}
              isLoadingMore={activityQ.isFetchingNextPage}
              onLoadMore={() => void activityQ.fetchNextPage()}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
